import { useState } from 'react';
import CreateEscalationButton from './CreateEscalationButton';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import useAnalytics from '../../analytics/analytics';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Pencil, Plus, Trash2 } from 'lucide-react';

interface EscalationButton {
    id: string;
    caption: string;
    instructions: string;
}

const EscalationButtons = ({
    escalationButtons,
    setEscalationButtons,
    setNeedsUpdate,
    agId,
    agentName,
    escalationEnabled,
    setEscalationEnabled
}: {
    escalationButtons: EscalationButton[],
    setEscalationButtons: (buttons: EscalationButton[]) => void,
    setNeedsUpdate: (value: boolean) => void,
    agId: string | undefined,
    agentName: string | undefined,
    escalationEnabled: boolean,
    setEscalationEnabled: (enabled: boolean) => void
}) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [activeButton, setActiveButton] = useState<EscalationButton | null>(null);
    const { captureEvent } = useAnalytics();

    const toggleModal = (button?: EscalationButton) => {
        if (modalVisible) {
            setActiveButton(null);
        } else {
            setActiveButton(button || null);
        }
        setModalVisible(!modalVisible);
    };

    const addButton = (caption: string, instructions: string) => {
        if (caption === "" || instructions === "") {
            toast.error('Both caption and instructions are required', toastConfig);
            return false;
        }

        if (escalationButtons.length >= 2) {
            toast.error('You can only have 2 escalation buttons', toastConfig);
            return false;
        }

        const button = {
            id: uuidv4(),
            caption,
            instructions
        };
        setEscalationButtons([...escalationButtons, button]);
        setNeedsUpdate(true);
        captureEvent('escalation_button_added', {
            agent_id: agId,
            agent_name: agentName,
        });
        return true;
    };

    const editButton = (id: string, caption: string, instructions: string) => {
        const updatedButtons = escalationButtons.map(button => 
            button.id === id ? { ...button, caption, instructions } : button
        );
        setEscalationButtons(updatedButtons);
        setNeedsUpdate(true);
    };

    const deleteButton = (id: string) => {
        setEscalationButtons(escalationButtons.filter(button => button.id !== id));
        setNeedsUpdate(true);
        captureEvent('escalation_button_deleted', {
            agent_id: agId,
            agent_name: agentName,
        });
    };

    return (
        <div className="divide-y">
            <div className="flex items-center justify-between gap-6 p-4">
                <div>
                    <h4 className="text-sm font-medium">Enable escalation buttons</h4>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        Show visitors actions that redirect the conversation to another path.
                    </p>
                </div>
                <Switch
                    checked={escalationEnabled}
                    onCheckedChange={setEscalationEnabled}
                    className="shrink-0"
                />
            </div>

            {escalationEnabled && (
                <>
                    {escalationButtons.length > 0 && (
                        <div className="divide-y">
                            {escalationButtons.map((button) => (
                                <div
                                    key={button.id}
                                    className="group flex min-h-12 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/30"
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleModal(button)}
                                        className="min-w-0 flex-1 text-left"
                                    >
                                        <p className="truncate text-sm font-medium">{button.caption}</p>
                                        <p className="truncate text-xs text-muted-foreground">{button.instructions}</p>
                                    </button>
                                    <div className="flex shrink-0 items-center gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                            onClick={() => toggleModal(button)}
                                            aria-label={`Edit ${button.caption}`}
                                        >
                                            <Pencil className="h-3 w-3" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                            onClick={() => deleteButton(button.id)}
                                            aria-label={`Delete ${button.caption}`}
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-3 p-3">
                        <span className="text-xs text-muted-foreground">
                            {escalationButtons.length} of 2 buttons
                        </span>
                        {escalationButtons.length < 2 ? (
                            <Button type="button" variant="outline" size="sm" onClick={() => toggleModal()}>
                                <Plus className="mr-1.5 h-3.5 w-3.5" />
                                Add button
                            </Button>
                        ) : (
                            <span className="text-xs text-muted-foreground">Maximum reached</span>
                        )}
                    </div>
                </>
            )}

            {modalVisible && (
                <CreateEscalationButton
                    addButton={addButton}
                    editButton={editButton}
                    setModalVisible={setModalVisible}
                    button={activeButton}
                />
            )}
        </div>
    );
};

export default EscalationButtons; 